import React, { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import flowFrameLogo from "../data/FlowFrame Logo.png";

export function AboutTab() {
  // State for Part 1 collapsible sections
  const [openSectionsPart1, setOpenSectionsPart1] = useState(
    Array(8).fill(false)
  );

  // Toggle function for Part 1
  function toggleSectionPart1(idx) {
    setOpenSectionsPart1((prev) =>
      prev.map((isOpen, i) => (i === idx ? !isOpen : isOpen))
    );
  }

  // State for Part 2 collapsible sections
  const [openSectionsPart2, setOpenSectionsPart2] = useState(
    Array(6).fill(false)
  );

  // Toggle function for Part 2
  function toggleSectionPart2(idx) {
    setOpenSectionsPart2((prev) =>
      prev.map((isOpen, i) => (i === idx ? !isOpen : isOpen))
    );
  }

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    inquiryType: "question",
    message: "",
  });
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // Handle contact form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm({
      ...contactForm,
      [name]: value,
    });
  };
  
  // Handle contact form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Set form as submitted to show success message
    // Form will be submitted by the form action
    setFormSubmitted(true);
    
    // Reset the form after submission (will happen after redirect back)
    setTimeout(() => {
      setFormSubmitted(false);
    }, 5000);
    
    // Allow the form to submit naturally to Formspree
    // by accessing the form element and submitting it
    e.target.submit();
  };

  // Part 1 panel data (after the first paragraph)
  const panelDataPart1 = [
    {
      title: "FlowFrame: A Groundbreaking Practice Tool",
      content: (
        <p className="mb-4">
          FlowFrame is a groundbreaking metronome and practice environment
          designed to meet the evolving needs of today's musicians—students,
          professionals, instructors, and performers of every genre. By blending
          intuitive controls with deep customization, FlowFrame goes far beyond
          the traditional "tick-tock" to support the most demanding practice
          goals, helping you focus on the music rather than the mechanics.
        </p>
      ),
    },
    {
      title: "1. Layered Time & Polyrhythms",
      content: (
        <p className="mb-4">
          Tackle complex rhythms head-on with layered click tracks that
          independently manage tempo changes, odd meters, and polyrhythms. This
          lets you rehearse nuanced passages in everything from classical
          ensemble repertoire to jazz improvisation and progressive rock—all
          within one streamlined tool.
        </p>
      ),
    },
    {
      title: "2. Adaptive Tempo & Dynamic Accents",
      content: (
        <p className="mb-4">
          FlowFrame can gradually speed up or slow down a passage to help you
          build muscle memory and accuracy over time. Define accent patterns
          within a bar—perfect for emphasizing the swing in a jazz line or
          shaping a phrase in a string quartet. These "tempo ramps" and
          fine-tuned accents give you more control than ever over your tempo
          map.
        </p>
      ),
    },
    {
      title: "3. Practice Tools & Progress Tracking",
      content: (
        <p className="mb-4">
          Keep track of your daily practice sessions with built-in session
          logging. Mark tricky sections, measure your progress, and store custom
          presets so you can revisit or modify them later. This level of detail
          helps you or your students focus on consistent, mindful improvement.
        </p>
      ),
    },
    {
      title: "4. Sound Customization & Multi-Device Support",
      content: (
        <p className="mb-4">
          Swap out the click sound for something that suits your instrument or
          musical style. Create distinct tracks for different sections of your
          piece, or use multiple devices synced together—ideal for rehearsals
          and group practice in large ensembles or workshop settings.
        </p>
      ),
    },
    {
      title: "5. Collaborative Features",
      content: (
        <p className="mb-4">
          Share tempo patterns, piece-specific presets, or complex metrical
          studies with colleagues and students. Instructors can push real-time
          tempo changes or new polyrhythm challenges to their entire class,
          ensuring everyone is on the same page—literally and figuratively.
        </p>
      ),
    },
    {
      title: "6. Ease of Use & Versatility",
      content: (
        <p className="mb-4">
          FlowFrame's interface is accessible enough for a middle school band
          student while remaining powerful for advanced university-level
          practice rooms and professional orchestras. Jazz improvisers love the
          ability to manage shifting harmonies, while classical musicians
          appreciate the precision and reliability during solo and ensemble
          work.
        </p>
      ),
    },
    {
      title: "Conclusion",
      content: (
        <p>
          Whether you're an aspiring vocalist, a seasoned orchestral player, a
          gigging jazz artist, or someone exploring new rhythmic landscapes,
          FlowFrame is built to be the ultimate tempo and pitch partner—capable
          of evolving with your needs and supporting your personal approach to
          music-making. Let FlowFrame handle the mechanics of tempo, so you can
          focus on bringing your musical ideas to life.
        </p>
      ),
    },
  ];

  // Part 2 panel data (instructions)
  const panelDataPart2 = [
    {
      title: "1) Basic Use: Just Press Start",
      content: (
        <div>
          <p className="mb-2">
            For a simple metronome, set your desired BPM and press Start. The
            metronome will click on each beat according to your chosen time
            signature.
          </p>
          <p className="text-sm text-gray-600">
            Ideal for: Basic tempo practice, counting out loud, or warming up on
            scales.
          </p>
        </div>
      ),
    },
    {
      title: "2) Incremental Tempo Change",
      content: (
        <div>
          <p className="mb-2">
            Enable <strong>Tempo Changes</strong> and choose <em>Incremental</em> mode. Set your start and end tempo (e.g., 60 →
            120 BPM), and decide how many measures or loops you want before
            stepping up the tempo. This helps you gradually practice faster
            passages while building muscle memory.
          </p>
          <p className="mb-2">
            <strong>Tip:</strong> Use "whole pattern" increments for global
            stepping, or choose "specific bars" for local loops on a tricky
            section.
          </p>
        </div>
      ),
    },
    {
      title: "3) Mute Random Beats and Pitches to Internalize Music",
      content: (
        <div>
          <p className="mb-2">
            Toggle <strong>Mute Random Beats</strong> and set the percentage.
            After a few measures, FlowFrame randomly drops clicks so you have to
            maintain the beat internally.
          </p>
          <p className="mb-2">
            Similarly, use <strong>Mute Random Pitches</strong> to develop your
            pitch memory and audiation skills. This challenges you to maintain the
            melodic and harmonic context even when certain notes are absent.
          </p>
          <p className="text-sm text-gray-600">
            These techniques are great for developing strong internal time and pitch,
            training your musical mind to fill in the gaps when audible cues aren't present.
          </p>
        </div>
      ),
    },
    {
      title: "4) Practicing Phrasing with Stress Patterns & Dynamics",
      content: (
        <div>
          <p className="mb-2">
            Use <strong>Stress Patterns</strong> (toggle in the hamburger menu)
            to automatically accent downbeats. You can also manually set{" "}
            <em>pp, mf, ff</em> in the <em>Dynamic Markings</em> for each
            measure in the Pattern Overview. Add cresc. or dim. to shape your
            phrase across measures.
          </p>
          <p className="text-sm text-gray-600">
            Perfect for shaping musical lines, not just keeping a mechanical
            beat.
          </p>
        </div>
      ),
    },
    {
      title: "5) Use Advanced Pitch Features for Intonation and Harmony",
      content: (
        <div>
          <p className="mb-2">
            Take advantage of FlowFrame's comprehensive pitch tools to enhance your practice. Select from drones, harmonies, or any combination of notes spanning three octaves. Explore the overtone series or experiment with microtonality to develop your ear for extended harmonics and non-standard tunings.
          </p>
          <p className="text-sm text-gray-600">
            Most pitch features can be set to Just Intonation for practicing pure intervals, or kept in equal temperament for modern ensemble work. Overtones and microtonality features maintain their specialized tuning systems. Perfect for developing pitch accuracy, ear training, and harmonic awareness.
          </p>
        </div>
      ),
    },
    {
      title: "6) Create Patterns With Custom Modulation",
      content: (
        <div>
          <p className="mb-2">
            Build patterns that automatically modulate by any interval you choose. Whether you need to move up a half-step, a perfect fourth, or any other interval, FlowFrame supports it. Choose to loop your pattern or simply modulate through without repeating—perfect for practicing scales, arpeggios, or progressions across multiple keys to build range and key flexibility.
          </p>
          <p className="text-sm text-gray-600">
            Combine this with incremental tempo changes to gradually increase speed as you progress through different keys, creating a comprehensive practice routine.
          </p>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: "1rem" }}>
      {/* Part 1: Title & First Paragraph */}
      <div className="text-center mb-3">
        <img
          src={flowFrameLogo}
          alt="FlowFrame Logo"
          className="inline-block w-48"
        />
      </div>

      <h2 className="text-2 mb-4 text-center">Empowering Confident Artistry</h2>
      <h1 className="text-2xl font-bold mb-2 text-center">About FlowFrame</h1>
      <p className="mb-4">
        Musical confidence starts with two vital pillars: pitch and pulse.
        FlowFrame is a tool that helps you hone your sense of rhythm and improve
        your intonation, giving you the freedom to express yourself
        authentically. Let FlowFrame guide you as you grow, thrive, and truly
        flow with the music. Enjoy your journey!
      </p>

      {/* Link to jump to Part 2 Instructions */}
      <div className="text-center mb-6">
        <a
          href="#part2-instructions"
          className="underline text-blue-600 hover:text-blue-800"
        >
          Jump to Instructions
        </a>
        {" | "}
        <a
          href="#contact-section"
          className="underline text-blue-600 hover:text-blue-800"
        >
          Contact Us
        </a>
        {" | "}
        <a
          href="#creator-section"
          className="underline text-blue-600 hover:text-blue-800"
        >
          Meet the Creator
        </a>
        {" | "}
        <a
          href="/privacy.html"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-blue-600 hover:text-blue-800"
        >
          Privacy Policy
        </a>
      </div>

            {/* Michael Clayville Creator Section */}
            <div id="creator-section" className="mb-12 bg-gray-50 rounded-lg p-6 border border-gray-200">
        <h2 className="text-xl font-bold mb-6 text-center">Meet the Creator</h2>
        
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          {/* Michael's image */}
          <div className="flex-shrink-0">
            <img
              src="https://michaelclayville.com/wp-content/uploads/2020/02/IMG_20180507_120046_040.jpg"
              alt="Michael Clayville"
              className="w-48 h-48 object-cover rounded-lg shadow-md"
            />
          </div>
          
          {/* Content */}
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-3">Michael Clayville</h3>
            <p className="text-gray-700 mb-4">
              Michael Clayville is a GRAMMY® Award-winning professional trombonist, educator, and the creator of FlowFrame. 
              As a founding member of the acclaimed ensemble <a href="https://alarmwillsound.com" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer"><em>Alarm Will Sound</em></a>—called the "future of classical music" 
              by the <em>New York Times</em>—Michael has performed at prestigious venues worldwide including Carnegie Hall, 
              Walt Disney Concert Hall, and the Barbican in London.
            </p>
            
            <p className="text-gray-700 mb-4">
              Currently serving on the faculty at <a href="https://www.lawrence.edu/conservatory" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer"><em>Lawrence University</em></a>, Michael teaches courses on entrepreneurship 
              and innovation while directing the New Music Ensemble and coaching chamber groups. His unique 
              perspective as both a performing artist and educator drives his passion for creating tools that 
              serve real musical needs.
            </p>
            
            <p className="text-gray-700 mb-4">
              <strong>Why FlowFrame?</strong> "Throughout my career performing and teaching, I've seen how musicians need more than just a basic metronome. We need tools that help us manage our practice time effectively, track our progress, work on both rhythmic precision and pitch accuracy, and share our musical ideas with colleagues and students. Traditional practice tools often address only one aspect of musicianship, leaving gaps in our development."
            </p>
            
            <p className="text-gray-700 mb-4">
              "FlowFrame emerged from recognizing these diverse needs: a comprehensive practice environment that supports everything from daily practice organization and progress monitoring to complex polyrhythmic work and advanced intonation training. Beyond individual practice, it facilitates meaningful musical connections—whether sharing challenging passages with a colleague or guiding students through their practice journey. My goal was to create a tool that serves the complete musician, not just one aspect of musical development."
            </p>
            

            <p className="text-gray-700 mb-4">
              "FlowFrame emerged from this need: a practice tool that could grow with musicians, supporting everything 
              from a student's first scales to professional preparation of the most challenging repertoire. 
              My goal was to create something that enhances musical expression rather than constrains it."
            </p>
            
            <div className="mt-4 text-sm text-gray-600">
              <p>
                <strong>Education:</strong> Rice University (MM), Eastman School of Music (BM)
                <br />
                <strong>Website:</strong> <a href="https://michaelclayville.com" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">michaelclayville.com</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible sections for the rest of Part 1 */}
      {panelDataPart1.map((panel, idx) => (
        <div key={idx} className="mb-4 border border-gray-300 rounded">
          <button
            type="button"
            onClick={() => toggleSectionPart1(idx)}
            className="w-full flex items-center justify-between p-2 bg-gray-100 hover:bg-gray-200"
          >
            <span className="font-medium">{panel.title}</span>
            {openSectionsPart1[idx] ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          {openSectionsPart1[idx] && (
            <div className="p-3 text-sm bg-white text-gray-800">
              {panel.content}
            </div>
          )}
        </div>
      ))}

      {/* Part 2 Instructions at the bottom */}
      <div id="part2-instructions" className="mt-12">
        <h1 className="text-2xl font-bold mb-4 text-center">
          Instructions for Use
        </h1>
        <p className="mb-6 text-center text-gray-700">
          These quick tips will guide you from basic usage to advanced practice
          techniques.
        </p>

        {panelDataPart2.map((panel, idx) => (
          <div key={idx} className="mb-4 border border-gray-300 rounded">
            <button
              type="button"
              onClick={() => toggleSectionPart2(idx)}
              className="w-full flex items-center justify-between p-2 bg-gray-100 hover:bg-gray-200"
            >
              <span className="font-medium">{panel.title}</span>
              {openSectionsPart2[idx] ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
            {openSectionsPart2[idx] && (
              <div className="p-3 text-sm bg-white text-gray-800">
                {panel.content}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Contact Section */}
      <div id="contact-section" className="mt-16 mb-8">
        <h1 className="text-2xl font-bold mb-4 text-center">Contact Us</h1>
        <p className="mb-6 text-center text-gray-700">
          Have questions, suggestions, or need assistance with your subscription? We're here to help!
        </p>
        
        <div className="max-w-md mx-auto bg-white rounded-lg overflow-hidden shadow-lg p-6 border border-gray-300">
          {formSubmitted ? (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              <p>Thank you for contacting us! We'll get back to you soon.</p>
            </div>
          ) : null}
          
          <form 
            onSubmit={handleSubmit}
            action="https://formspree.io/f/manoegey"
            method="POST"
          >
            <div className="mb-4">
              <label htmlFor="name" className="block text-gray-700 text-sm font-bold mb-2">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={contactForm.name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={contactForm.email}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="inquiryType" className="block text-gray-700 text-sm font-bold mb-2">
                What can we help you with?
              </label>
              <select
                id="inquiryType"
                name="subject"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={contactForm.inquiryType}
                onChange={handleInputChange}
                required
              >
                <option value="Question about FlowFrame">I have a question</option>
                <option value="Suggestion for FlowFrame">I have a suggestion</option>
                <option value="Bug Report for FlowFrame">I found a bug</option>
                <option value="Subscription Cancellation Request">I want to cancel my subscription</option>
                <option value="FlowFrame Inquiry">Other</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label htmlFor="message" className="block text-gray-700 text-sm font-bold mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows="5"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={contactForm.message}
                onChange={handleInputChange}
                required
              ></textarea>
            </div>
            
            <div className="flex items-center justify-between">
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
              >
                Send Message
              </button>
            </div>
          </form>
        </div>
        
        <div className="text-center mt-6 text-sm text-gray-600">
          <p>You can also email us directly at <a href="mailto:flowframe@flowframe.app" className="text-blue-600 hover:underline">flowframe@flowframe.app</a></p>
        </div>
      </div>

      {/* Privacy Policy Link */}
      <div className="mt-12 mb-8 text-center">
        <a
          href="/privacy.html"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg px-6 py-3 text-gray-700 hover:text-gray-900 transition-colors"
        >
          <span className="font-semibold">Privacy Policy</span>
          <span className="block text-xs text-gray-500 mt-1">View our full privacy policy</span>
        </a>
      </div>
    </div>
  );
}

export default AboutTab;
